import { useDeleteLayer } from "../../hooks/useProjects";
import type { Layer } from "../../types/projects";

interface Props {
  projectId: string;
  layer: Layer;
  /** Number of site_plan_candidate rows routed from this layer. Shown
   * in the warning copy so the user understands the cascade. Omit
   * (or pass 0) when the layer isn't routed anywhere yet. */
  sitePlanCandidateCount?: number;
  /** True when this layer is the project's active boundary — the
   * cascade will null ``project.boundary_layer_id`` and clear the
   * stored boundary geometry. */
  isActiveBoundary?: boolean;
  onClose: () => void;
  /** Called after a successful delete (DB row + S3 prefix removed). */
  onDeleted: () => void;
}

/**
 * Delete-a-single-file confirmation modal.
 *
 * Same visual language as :func:`DeleteProjectModal` but lighter
 * weight — files are less destructive than whole projects, so no
 * "type the filename to confirm" gate. The modal still spells out the
 * cascade (site plan candidates removed, boundary cleared if active)
 * so the user knows what's about to happen.
 */
export function DeleteFileModal({
  projectId,
  layer,
  sitePlanCandidateCount,
  isActiveBoundary,
  onClose,
  onDeleted,
}: Props) {
  const mutation = useDeleteLayer(projectId);
  const filename = layer.original_filename || layer.name;

  const onConfirm = async () => {
    try {
      await mutation.mutateAsync(layer.id);
      onDeleted();
    } catch {
      // mutation.isError surfaces below
    }
  };

  // Build the cascade-summary sentence. Each addition only shows when
  // applicable — otherwise we'd render filler like "and 0 candidates".
  const cascadeParts: string[] = [];
  if (sitePlanCandidateCount && sitePlanCandidateCount > 0) {
    cascadeParts.push(
      `${sitePlanCandidateCount} site plan candidate${
        sitePlanCandidateCount === 1 ? "" : "s"
      } routed from it`,
    );
  }
  if (isActiveBoundary) {
    cascadeParts.push("the project's active boundary");
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-file-title"
      data-testid="delete-file-modal"
      style={styles.backdrop}
      onClick={(e) => {
        // Backdrop click closes — clicks inside the card stop here.
        if (e.target === e.currentTarget && !mutation.isPending) onClose();
      }}
    >
      <div style={styles.card}>
        <h2 id="delete-file-title" style={styles.title}>
          Delete this file?
        </h2>
        <p style={styles.body}>
          <strong>{filename}</strong>
          {cascadeParts.length > 0 && (
            <>
              {" "}
              and {cascadeParts.join(", ")} will be removed.
            </>
          )}{" "}
          This cannot be undone.
        </p>

        {mutation.isError && (
          <div role="alert" data-testid="delete-file-error" style={styles.error}>
            Couldn't delete the file. Please retry, or contact support if it
            keeps failing.
          </div>
        )}

        <div style={styles.actions}>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            data-testid="delete-file-cancel"
            style={styles.cancelLink}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={mutation.isPending}
            data-testid="delete-file-confirm-button"
            autoFocus
            style={mutation.isPending ? styles.dangerDisabled : styles.danger}
          >
            {mutation.isPending ? "Deleting…" : "Delete file"}
          </button>
        </div>
      </div>
    </div>
  );
}

const DANGER = "#dc2626";
const DANGER_HOVER = "#b91c1c";

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
    padding: "1rem",
  },
  card: {
    width: "100%",
    maxWidth: 460,
    background: "#ffffff",
    borderRadius: 12,
    padding: "1.75rem",
    boxShadow:
      "0 0 0 1px rgba(50, 50, 93, 0.04), 0 18px 36px -8px rgba(15, 23, 42, 0.18)",
  },
  title: {
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    margin: "0 0 0.75rem",
    color: "#0a0e27",
  },
  body: {
    fontSize: "0.9375rem",
    color: "#475569",
    lineHeight: 1.55,
    margin: "0 0 1rem",
    wordBreak: "break-word",
  },
  error: {
    background: "#fff7f5",
    color: "#9a3412",
    border: "1px solid #fed7aa",
    borderRadius: 6,
    padding: "0.625rem 0.75rem",
    fontSize: "0.8125rem",
    marginBottom: "0.75rem",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "1rem",
  },
  cancelLink: {
    background: "transparent",
    color: "#475569",
    border: "none",
    padding: "0.5rem 0.5rem",
    fontSize: "0.9375rem",
    cursor: "pointer",
  },
  danger: {
    background: DANGER,
    color: "#ffffff",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 120ms ease",
  },
  dangerDisabled: {
    background: DANGER_HOVER,
    color: "#ffffff",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: 6,
    fontSize: "0.9375rem",
    fontWeight: 500,
    opacity: 0.6,
    cursor: "not-allowed",
  },
};
